import { Link } from 'react-router-dom';
import Icon from './Icon';
import { SectionHead } from './Blocks';
import { IMG, u } from '../images';
import { STATS, TEAM } from '../data';

/* Full-width photo band with animated counters */
export function StatsBand() {
  return (
    <section className="relative overflow-hidden bg-ink py-20 text-white sm:py-24">
      <img src={u(IMG.band, 1600)} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/95 via-brand-dark/85 to-ink/90" />
      <div className="container-x relative grid gap-10 lg:grid-cols-[.9fr_1.1fr] lg:items-center">
        <div data-anim="left">
          <span className="eyebrow eyebrow-dark">Numbers that matter</span>
          <h2 className="h2 mt-5">Nine years of <span className="text-accent-light">shipping</span>, measured in <span className="text-outline-light">outcomes</span></h2>
        </div>
        <div className="grid grid-cols-2 gap-4" data-anim="right" data-stagger>
          {STATS.map((s) => (
            <div key={s.label} className="rounded-3xl border border-white/15 bg-white/[0.06] p-6 backdrop-blur">
              <p className="font-display text-4xl font-bold text-brand-lime sm:text-5xl" data-count={s.value} data-suffix={s.suffix}>0</p>
              <p className="mt-2 text-sm text-white/70">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const APPROACH = [
  { n: '01', title: 'Strategy first, pixels second', text: 'We start with your customers, goals and constraints so every design and engineering decision traces back to a business outcome.', points: ['Stakeholder workshops', 'Competitor and market review', 'Measurable success metrics'] },
  { n: '02', title: 'Design that earns attention', text: 'Interfaces and brand systems built around clarity, accessibility and a distinct visual voice — tested with real users before a line of code.', points: ['Clickable prototypes', 'Reusable design systems', 'Accessibility baked in'] },
  { n: '03', title: 'Engineering you can rely on', text: 'Clean, documented, well-tested code on modern infrastructure, shipped in small increments with a demo every week.', points: ['CI/CD and automated tests', 'Performance and security reviews', 'You own all the code'] },
];

// Layered decorative shapes that sit behind each approach photo (palette colours, SVG patterns).
function Dots({ id, color, className }) {
  return (
    <svg className={`absolute ${className}`} width="120" height="120" aria-hidden="true">
      <defs><pattern id={id} width="15" height="15" patternUnits="userSpaceOnUse"><circle cx="2.5" cy="2.5" r="2.5" fill={color} /></pattern></defs>
      <rect width="120" height="120" fill={`url(#${id})`} />
    </svg>
  );
}

function Backdrop({ v }) {
  if (v === 0) {
    return (
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute bottom-0 left-0 h-[78%] w-[78%] rounded-[2.5rem] bg-brand" />
        <div className="absolute right-0 top-0 h-28 w-28 rounded-full bg-brand-soft" />
        <Dots id="dots-a" color="#116466" className="left-0 top-0" />
        <div className="absolute bottom-2 right-3 h-20 w-20 rounded-full border-[10px] border-brand-tan" />
        <svg className="absolute right-10 top-[46%] hidden sm:block" width="34" height="34" viewBox="0 0 34 34" fill="none" stroke="#116466" strokeWidth="4" strokeLinecap="round"><path d="M17 4v26M4 17h26" /></svg>
      </div>
    );
  }
  if (v === 1) {
    return (
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute right-0 top-0 h-[80%] w-[80%] rotate-3 rounded-[3rem] bg-ink" />
        <div className="absolute bottom-3 left-0 h-24 w-24 rounded-full border-[12px] border-brand" />
        <Dots id="dots-b" color="#116466" className="bottom-0 right-0" />
        <div className="absolute left-3 top-10 h-16 w-16 -rotate-12 rounded-2xl bg-brand-tan" />
        <div className="absolute left-12 top-2 h-8 w-8 rounded-full bg-brand" />
      </div>
    );
  }
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute bottom-0 left-0 h-[80%] w-[78%] rounded-t-[999px] rounded-b-[2.5rem] bg-brand-soft" />
      <div className="absolute right-0 top-0 h-28 w-28 rounded-full bg-brand" />
      <div className="absolute bottom-0 right-0 h-24 w-24 animate-spinslow rounded-full border-2 border-dashed border-brand/60" />
      <Dots id="dots-c" color="#116466" className="bottom-6 right-6 opacity-60" />
    </div>
  );
}

export function Approach() {
  return (
    <section className="section bg-grad-soft">
      <div className="container-x">
        <SectionHead eyebrow="Our approach" title="How we turn ideas into products people use" text="Three disciplines, one accountable team — from the first workshop to the production launch." />
        <div className="mt-16 space-y-24">
          {APPROACH.map((a, i) => (
            <div key={a.n} className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
              <div className={`relative p-6 sm:p-9 ${i % 2 ? 'lg:order-2' : ''}`} data-anim={i % 2 ? 'right' : 'left'}>
                <Backdrop v={i} />
                <div className="relative">
                  <div className={`overflow-hidden shadow-2xl ${i % 2 ? 'rounded-[2rem] rounded-tl-[7rem]' : 'rounded-[2rem] rounded-tr-[7rem]'}`}>
                    <img src={u(IMG.approach[i], 1000)} alt="" loading="lazy" className="aspect-[4/3] w-full object-cover" />
                  </div>
                  <span className="absolute -bottom-5 left-6 rounded-2xl bg-ink px-5 py-3 font-display text-3xl font-bold text-brand-lime shadow-xl">{a.n}</span>
                </div>
              </div>
              <div data-anim={i % 2 ? 'left' : 'right'}>
                <h3 className="font-display text-2xl font-bold text-ink sm:text-3xl">{a.title}</h3>
                <p className="mt-4 text-lg leading-relaxed text-slate-600">{a.text}</p>
                <ul className="mt-6 space-y-3">
                  {a.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-slate-700">
                      <span className="grid h-6 w-6 place-items-center rounded-full bg-brand text-white"><Icon name="check" className="h-3.5 w-3.5" strokeWidth={3} /></span>{p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Photo mosaic */
export function Gallery() {
  const spans = ['sm:col-span-2 sm:row-span-2', '', '', 'sm:col-span-2', '', ''];
  return (
    <section className="section bg-ink text-white">
      <div className="grid-bg pointer-events-none absolute inset-0" />
      <div className="container-x relative">
        <SectionHead dark eyebrow="Inside Origo" title="The people and spaces behind the work" text="A collaborative studio culture where designers, engineers and strategists sit at the same table." />
        <div className="mt-14 grid auto-rows-[180px] grid-cols-2 gap-4 sm:grid-cols-4" data-anim="zoom" data-stagger>
          {IMG.gallery.map((id, i) => (
            <div key={id} className={`group relative overflow-hidden rounded-3xl ${spans[i]}`}>
              <img src={u(id, 900)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-brand/20 opacity-70 transition-opacity group-hover:opacity-30" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TeamStrip() {
  return (
    <section className="section bg-brand-soft">
      <div className="container-x">
        <SectionHead eyebrow="Our team" title="Senior specialists, not a revolving door of juniors" text="The people you meet in the first call are the ones who build your product." />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-anim="up" data-stagger>
          {TEAM.map((m, i) => (
            <article key={m.name} className="lift group overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-brand/10 transition-shadow hover:shadow-2xl">
              <div className="relative aspect-[4/5] overflow-hidden bg-brand-soft">
                <img src={u(IMG.team[i % IMG.team.length], 900)} alt={m.name} loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-brand-dark/70 to-transparent" />
                <span className="absolute bottom-4 left-4 rounded-full bg-brand-lime px-3 py-1 text-xs font-semibold text-ink">{m.role}</span>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-ink">{m.name}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{m.bio}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CtaBanner() {
  return (
    <section className="relative overflow-hidden bg-ink py-24 text-white">
      <img src={u(IMG.cta, 1600)} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-br from-brand-dark/95 via-ink/90 to-ink/95" />
      <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-brand-lime/20 blur-[120px]" />
      <div className="container-x relative text-center" data-anim="zoom">
        <span className="eyebrow eyebrow-dark">Let's build together</span>
        <h2 className="h2 mx-auto mt-5 max-w-3xl">Ready to bring your next <span className="text-accent-light">idea</span> to <span className="text-outline-light">life</span>?</h2>
        <p className="mx-auto mt-5 max-w-xl text-lg text-white/70">Book a free 30-minute consultation. We'll review your goals and send a fixed-price proposal within three business days.</p>
        <div className="mt-9 flex flex-wrap justify-center gap-4">
          <Link to="/contact" className="btn-primary">Book a free consultation <Icon name="arrow" className="h-4 w-4" /></Link>
          <Link to="/portfolio" className="btn-ghost">See our work</Link>
        </div>
      </div>
    </section>
  );
}
